import classNames from 'classnames'
import { CSSProperties, useEffect, useMemo, useState } from 'react'

import { t } from '../../../../common/i18n.js'
import {
    PlaytimeBadgeCategory,
    PlaytimeLeaderboardData,
    PlaytimeProfileData,
    PlaytimeUserBadge,
} from '../../../../common/types/playtime.js'
import { sortAnniversaryBadges, sortRegularBadges } from '../../../../common/utils/badge-sort.js'
import { BadgeCard } from '../../../components/badge-card/badge-card.js'
import { BadgeModal } from '../../../components/badge-modal/badge-modal.js'
import { RevealPhase } from '../../../components/badge-reveal-stage/badge-reveal-stage.js'
import { Button } from '../../../components/button/button.js'
import {
    ArrowLeft,
    ArrowRight,
    ChevronDown,
    SlidersHorizontal,
} from '../../../components/icon/solar-icons.js'
import { LevelProgress } from '../../../components/level-progress/level-progress.js'
import { FilterSheet } from '../../../components/filter-sheet/filter-sheet.js'
import { Link } from '../../../components/link/link.js'
import { OptionSheet } from '../../../components/option-sheet/option-sheet.js'
import { BaseModal } from '../../../components/modal/base-modal/base-modal.js'
import { PageHeader } from '../../../components/page-header/page-header.js'
import { PageShell } from '../../../components/page-shell/page-shell.js'
import { Spinner } from '../../../components/spinner/spinner.js'
import { Tabs } from '../../../components/tabs/tabs.js'
import { ToggleGroup } from '../../../components/toggle-group/toggle-group.js'
import { UserBubble } from '../../../components/user-bubble/user-bubble.js'
import { DataState, View } from '../../../types.js'
import {
    BADGE_SIZE_PX,
    BadgeSize,
    loadBadgeSize,
    persistBadgeSize,
} from '../../../utils/badge-size.js'
import {
    BADGE_SORTS,
    BadgeSort,
    loadBadgeSort,
    persistBadgeSort,
} from '../../../utils/badge-sort-pref.js'
import { refreshPlaytimeProfile } from '../../../utils/load-utils.js'
import { notify } from '../../../utils/notification-utils.js'
import { ViewBaseProps } from '../../base/base.js'
import { uploadClaimFiles } from '../badge-claim-upload.js'
import { isTextEntryTarget, nextBadgeId } from '../badge-nav.js'
import '../playtime-layout.css'
import { getLevelProgressSubtitle } from '../playtime-utils.js'
import './profile.css'

export interface PlaytimeProfileViewProps {
    base: ViewBaseProps
    profile: DataState<PlaytimeProfileData>
    leaderboard: DataState<PlaytimeLeaderboardData>
    totalBadgesCount: number
    view: View
    /** Set when viewing another user's profile: read-only, back link instead of tabs. */
    publicProfile?: {
        userId: number
        userName: string | null
    }
}

type BadgeFilter = 'all' | 'active' | 'inactive' | 'pending' | 'new'

/** Badges created within the last 3 months are considered "new" in the filter */
const THREE_MONTHS_MS = 90 * 24 * 60 * 60 * 1000
/** Delay before a fresh badge starts its unlock animation after the modal opens */
const REVEAL_START_MS = 300
const BADGE_SIZES: BadgeSize[] = ['S', 'M', 'L']

const isNewBadge = (badge: PlaytimeUserBadge): boolean => {
    if (!badge.created_at_utc) return false
    return Date.now() - new Date(badge.created_at_utc).getTime() < THREE_MONTHS_MS
}

const getUniqueCategories = (badges: PlaytimeUserBadge[]): PlaytimeBadgeCategory[] => {
    const map = new Map<number, PlaytimeBadgeCategory>()
    for (const badge of badges) for (const cat of badge.categories) map.set(cat.id, cat)
    return [...map.values()].sort((a, b) => a.name_en.localeCompare(b.name_en))
}

/** A category chip is either a real category id, one of the two flag-filters, or "all". */
type CategoryFilter = number | 'special' | 'anniversary' | null

const sortLabel = (sort: BadgeSort) => {
    switch (sort) {
        case 'points-desc':
            return t.playtime.badges.sortPointsDesc
        case 'points-asc':
            return t.playtime.badges.sortPointsAsc
        default:
            return t.playtime.badges.sortName
    }
}

const prefersReducedMotion = (): boolean =>
    typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches

export const PlaytimeProfileView = (props: PlaytimeProfileViewProps) => {
    const { view, publicProfile } = props
    const lang = view.state.lang
    const [selectedId, setSelectedId] = useState<number | null>(null)
    const [badgeFilter, setBadgeFilter] = useState<BadgeFilter>(publicProfile ? 'active' : 'all')
    const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>(null)
    const [size, setSize] = useState<BadgeSize>(loadBadgeSize)
    const [sort, setSort] = useState<BadgeSort>(loadBadgeSort)
    /** Mobile: the sort lives in a bottom sheet behind the "Nimi ▾" trigger (Nele, 22.10.2026). */
    const [isSortSheetOpen, setSortSheetOpen] = useState(false)
    const [isFilterSheetOpen, setFilterSheetOpen] = useState(false)
    const [isPeeking, setPeeking] = useState(false)
    const [revealPhase, setRevealPhase] = useState<RevealPhase | null>(null)
    const [seenIds, setSeenIds] = useState<Set<number>>(() => new Set())
    const [claimComment, setClaimComment] = useState('')
    const [claimFiles, setClaimFiles] = useState<File[]>([])
    const [isSubmitting, setSubmitting] = useState(false)

    const { data, isLoading } = props.profile
    const { data: leaderboardData, isLoading: isLoadingLeaderboard } = props.leaderboard

    // Deep link #badge-<id> (Slack/e-mail): open that badge, clear filters.
    useEffect(() => {
        const match = location.hash.match(/^#badge-(\d+)$/)
        if (!match || !data) return
        const id = Number(match[1])
        if (data.badges.some((b) => b.id === id)) {
            setBadgeFilter('all')
            setCategoryFilter(null)
            setSelectedId(id)
            history.replaceState(null, '', location.pathname)
        }
    }, [data])

    const availableCategories = useMemo(
        () => getUniqueCategories(data?.badges ?? []),
        [data?.badges],
    )

    // Badges the owner has already unlocked in this session no longer count as new.
    const allBadges = useMemo(
        () => (data?.badges ?? []).map((b) => (seenIds.has(b.id) ? { ...b, is_new: false } : b)),
        [data?.badges, seenIds],
    )
    const selectedBadge = allBadges.find((b) => b.id === selectedId) ?? null

    const markSeen = (id: number) => {
        setSeenIds((prev) => new Set(prev).add(id))
        view.api.playtime.markBadgeSeen(id).catch(() => {})
    }

    // Fresh badge: start the unlock shortly after the modal opens (reduced motion: skip to done).
    const selectedIsNew = !!selectedBadge?.is_new && !publicProfile
    useEffect(() => {
        if (!selectedIsNew || selectedId === null) {
            setRevealPhase(null)
            return
        }
        if (prefersReducedMotion()) {
            setRevealPhase('done')
            markSeen(selectedId)
            return
        }
        setRevealPhase('locked')
        const timer = setTimeout(() => setRevealPhase('revealing'), REVEAL_START_MS)
        return () => clearTimeout(timer)
        // markSeen is stable for the view's lifetime; re-run only when the selection changes
    }, [selectedId, selectedIsNew])

    const filterBadges = (badges: PlaytimeUserBadge[]) => {
        let result = badges
        switch (badgeFilter) {
            case 'active':
                result = result.filter((b) => b.status === 'claimed')
                break
            case 'inactive':
                result = result.filter((b) => b.status === null || b.status === 'unclaimed')
                break
            case 'pending':
                result = result.filter((b) => b.status === 'pending')
                break
            case 'new':
                result = result.filter(isNewBadge)
                break
        }
        if (categoryFilter === 'special') result = result.filter((b) => b.is_special)
        else if (categoryFilter === 'anniversary') result = result.filter((b) => b.is_anniversary)
        else if (categoryFilter !== null)
            result = result.filter((b) => b.categories.some((c) => c.id === categoryFilter))
        return result
    }

    const regularBadges = sortRegularBadges(
        allBadges.filter((b) => !b.is_anniversary),
        sort,
    )
    const anniversaryBadges = sortAnniversaryBadges(allBadges.filter((b) => b.is_anniversary))
    const filteredRegular = filterBadges(regularBadges)
    const filteredAnniversary = filterBadges(anniversaryBadges)
    const visibleIds = [...filteredRegular, ...filteredAnniversary].map((b) => b.id)
    const freshBadges = publicProfile ? [] : allBadges.filter((b) => b.is_new)
    const isRevealing = revealPhase === 'revealing'

    const openBadge = (id: number) => {
        setPeeking(false)
        setClaimComment('')
        setClaimFiles([])
        setSelectedId(id)
    }
    const closeModal = () => {
        if (isRevealing) return
        setSelectedId(null)
    }
    const step = (dir: 1 | -1) => {
        if (selectedId === null || isRevealing) return
        const next = nextBadgeId(visibleIds, selectedId, dir)
        if (next !== null) openBadge(next)
    }

    // Arrow keys move between badges while the modal is open (not inside inputs).
    useEffect(() => {
        if (selectedId === null) return
        const onKey = (e: KeyboardEvent) => {
            const target = e.target as HTMLElement | null
            if (isTextEntryTarget(target?.tagName, target?.getAttribute('type') ?? null)) return
            if (e.key === 'ArrowLeft') step(-1)
            else if (e.key === 'ArrowRight') step(1)
        }
        document.addEventListener('keydown', onKey)
        return () => document.removeEventListener('keydown', onKey)
    })

    const handleSizeChange = (next: BadgeSize) => {
        setSize(next)
        persistBadgeSize(next)
    }

    const handleSortChange = (next: BadgeSort) => {
        setSort(next)
        persistBadgeSort(next)
    }

    const submitClaim = async () => {
        if (!selectedBadge || !claimComment.trim()) return
        setSubmitting(true)
        let fileUrls: string[]
        try {
            fileUrls = await uploadClaimFiles(claimFiles)
        } catch {
            notify({ view, type: 'error', text: t.playtime.badges.uploadFailed(lang) })
            setSubmitting(false)
            return
        }
        try {
            await view.api.playtime.createBadgeRequest({
                badge_id: selectedBadge.id,
                description: claimComment,
                file: fileUrls.length > 0 ? JSON.stringify(fileUrls) : undefined,
            })
            notify({ view, type: 'success', text: t.playtime.notifications.requestCreated(lang) })
            setClaimComment('')
            setClaimFiles([])
            // Keeps the modal open: the badge flips to "Ootel kinnitamist" when the data arrives.
            await refreshPlaytimeProfile(view)
        } catch {
            // httpSend already notified; the form keeps its content for a retry
        } finally {
            setSubmitting(false)
        }
    }

    if (isLoading || isLoadingLeaderboard || !data || !leaderboardData) {
        return <Spinner />
    }

    const rank = publicProfile
        ? leaderboardData.users.findIndex((u) => u.id === publicProfile.userId) + 1
        : leaderboardData.currentUserRank
    const subtitle = publicProfile
        ? t.playtime.publicProfile.rank(rank)(lang)
        : getLevelProgressSubtitle(data.totalScore, data.level, data.nextThreshold, rank, lang)

    const statusFilters: BadgeFilter[] = publicProfile
        ? ['all', 'active', 'inactive', 'new']
        : ['all', 'active', 'inactive', 'pending', 'new']
    const tabItems = [
        { key: 'badges', label: t.playtime.levelProgress.badges(lang), href: '/playtime' },
        { key: 'leaderboard', label: t.playtime.leaderboard(lang), href: '/playtime/leaderboard' },
    ]

    const sortOptions = BADGE_SORTS.map((value) => ({ value, label: sortLabel(value)(lang) }))
    const activeFilterCount = (badgeFilter === 'all' ? 0 : 1) + (categoryFilter === null ? 0 : 1)

    // Under 1000 px (Nele's variant C, 22.10.2026) the toolbar is a "Filtrid (n)" button and a
    // "Nimi ▾" trigger, both opening bottom sheets; the chosen filters sit as removable chips
    // in the bar above the grid. Desktop keeps the segmented controls and the chip row.
    const toolbar = (
        <div className="playtime-profile__toolbar">
            <div className="playtime-profile__toolbar-desktop">
                <ToggleGroup<BadgeFilter>
                    options={statusFilters.map((f) => ({
                        value: f,
                        label: t.playtime.filter[f](lang),
                    }))}
                    value={badgeFilter}
                    onChange={setBadgeFilter}
                />
                <ToggleGroup<BadgeSize>
                    ariaLabel={t.playtime.badges.size(lang)}
                    options={BADGE_SIZES.map((s) => ({ value: s, label: s }))}
                    value={size}
                    onChange={handleSizeChange}
                />
                <ToggleGroup<BadgeSort>
                    ariaLabel={t.playtime.badges.sort(lang)}
                    options={sortOptions}
                    value={sort}
                    onChange={handleSortChange}
                />
            </div>
            <div className="playtime-profile__toolbar-mobile">
                <button
                    type="button"
                    className={classNames(
                        'playtime-profile__filter-trigger',
                        activeFilterCount > 0 && 'playtime-profile__filter-trigger--active',
                    )}
                    aria-haspopup="dialog"
                    aria-expanded={isFilterSheetOpen}
                    onClick={() => setFilterSheetOpen(true)}
                >
                    <SlidersHorizontal size={16} />
                    {t.playtime.badges.filters(lang)}
                    {activeFilterCount > 0 && (
                        <span className="playtime-profile__filter-count">{activeFilterCount}</span>
                    )}
                </button>
                <button
                    type="button"
                    className="playtime-profile__sort-trigger"
                    aria-haspopup="dialog"
                    aria-expanded={isSortSheetOpen}
                    onClick={() => setSortSheetOpen(true)}
                >
                    {sortLabel(sort)(lang)}
                    <ChevronDown size={16} />
                </button>
                <OptionSheet<BadgeSort>
                    isOpen={isSortSheetOpen}
                    title={t.playtime.badges.sort(lang)}
                    options={sortOptions}
                    value={sort}
                    onChange={handleSortChange}
                    onClose={() => setSortSheetOpen(false)}
                />
            </div>
        </div>
    )

    const canClaim =
        !!selectedBadge &&
        !publicProfile &&
        selectedBadge.status !== 'claimed' &&
        selectedBadge.status !== 'pending' &&
        !selectedBadge.is_anniversary &&
        !selectedBadge.is_special

    const renderGrid = (badges: PlaytimeUserBadge[]) => (
        <div
            className={classNames(
                'playtime-profile__badges-grid',
                `playtime-profile__badges-grid--${size.toLowerCase()}`,
            )}
            style={{ '--badge-size': `${BADGE_SIZE_PX[size]}px` } as CSSProperties}
        >
            {badges.map((badge) => (
                <BadgeCard
                    key={badge.id}
                    badge={badge}
                    lang={lang}
                    onClick={() => openBadge(badge.id)}
                />
            ))}
        </div>
    )

    const categoryButton = (id: CategoryFilter, label: string) => (
        <button
            key={id ?? 'all'}
            type="button"
            className={classNames(
                'playtime-profile__filter-btn',
                categoryFilter === id && 'playtime-profile__filter-btn--active',
            )}
            onClick={() => setCategoryFilter(id)}
        >
            {label}
        </button>
    )

    // The two admin flags appear as ordinary filter chips: the user sees 6 groups, not the
    // behaviour behind them (prototype/Marianne, TASK-611).
    const hasSpecial = allBadges.some((b) => b.is_special)
    const hasAnniversary = allBadges.some((b) => b.is_anniversary)

    const categoryLabel = (id: CategoryFilter): string => {
        if (id === null) return t.playtime.filter.all(lang)
        if (id === 'special') return t.playtime.badges.special(lang)
        if (id === 'anniversary') return t.playtime.anniversaryDivider(lang)
        const cat = availableCategories.find((c) => c.id === id)
        return cat ? (lang === 'et' ? cat.name_et : cat.name_en) : ''
    }
    const categoryOptions: CategoryFilter[] = [
        null,
        ...availableCategories.map((c): CategoryFilter => c.id),
        ...(hasSpecial ? (['special'] as CategoryFilter[]) : []),
        ...(hasAnniversary ? (['anniversary'] as CategoryFilter[]) : []),
    ]
    const categoryKey = (id: CategoryFilter) => (id === null ? 'all' : String(id))
    const categoryFromKey = (key: string): CategoryFilter =>
        key === 'all' ? null : key === 'special' || key === 'anniversary' ? key : Number(key)
    const visibleCount = visibleIds.length

    const mobileBar = (
        <div className="playtime-profile__mobile-bar">
            <div className="playtime-profile__active-filters">
                {badgeFilter !== 'all' && (
                    <button
                        type="button"
                        className="playtime-profile__active-chip"
                        aria-label={t.playtime.badges.removeFilter(
                            t.playtime.filter[badgeFilter](lang),
                        )(lang)}
                        onClick={() => setBadgeFilter('all')}
                    >
                        {t.playtime.filter[badgeFilter](lang)} <span aria-hidden="true">×</span>
                    </button>
                )}
                {categoryFilter !== null && (
                    <button
                        type="button"
                        className="playtime-profile__active-chip"
                        aria-label={t.playtime.badges.removeFilter(categoryLabel(categoryFilter))(
                            lang,
                        )}
                        onClick={() => setCategoryFilter(null)}
                    >
                        {categoryLabel(categoryFilter)} <span aria-hidden="true">×</span>
                    </button>
                )}
            </div>
            <span className="playtime-profile__count">
                {t.playtime.badges.count(visibleCount)(lang)}
            </span>
            <FilterSheet
                isOpen={isFilterSheetOpen}
                title={t.playtime.badges.filters(lang)}
                clearLabel={t.playtime.badges.clearFilters(lang)}
                onClear={() => {
                    setBadgeFilter('all')
                    setCategoryFilter(null)
                }}
                sections={[
                    {
                        key: 'status',
                        label: t.playtime.badges.status(lang),
                        options: statusFilters.map((f) => ({
                            value: f,
                            label: t.playtime.filter[f](lang),
                        })),
                        value: badgeFilter,
                        onChange: (v) => setBadgeFilter(v as BadgeFilter),
                    },
                    {
                        key: 'category',
                        label: t.playtime.badges.category(lang),
                        options: categoryOptions.map((id) => ({
                            value: categoryKey(id),
                            label: categoryLabel(id),
                        })),
                        value: categoryKey(categoryFilter),
                        onChange: (v) => setCategoryFilter(categoryFromKey(v)),
                    },
                    {
                        key: 'sort',
                        label: t.playtime.badges.sort(lang),
                        options: sortOptions,
                        value: sort,
                        onChange: (v) => handleSortChange(v as BadgeSort),
                    },
                ]}
                applyLabel={t.playtime.badges.showCount(visibleCount)(lang)}
                onClose={() => setFilterSheetOpen(false)}
            />
        </div>
    )

    return (
        <PageShell className="playtime-page">
            <PageHeader
                title={
                    publicProfile ? (
                        <span className="playtime-profile__header-title">
                            <UserBubble name={null} avatar={data.avatarUrl} size="small" lang={lang} />
                            {publicProfile.userName ?? t.playtime.profile(lang)}
                        </span>
                    ) : (
                        t.playtime.profile(lang)
                    )
                }
            />

            <LevelProgress
                score={data.totalScore}
                level={data.level}
                nextThreshold={data.nextThreshold}
                claimedBadgesCount={data.claimedBadgesCount}
                totalBadgesCount={props.totalBadgesCount}
                lang={lang}
                subtitle={rank > 0 ? subtitle : undefined}
            />

            {publicProfile ? (
                <div className="playtime-profile__public-bar">
                    <Link className="playtime-profile__back-link" href="/playtime/leaderboard">
                        ← {t.playtime.publicProfile.back(lang)}
                    </Link>
                    {toolbar}
                </div>
            ) : (
                <Tabs
                    variant="underline"
                    items={tabItems}
                    activeKey="badges"
                    actions={toolbar}
                    lang={lang}
                />
            )}

            <div className="playtime__panel">
                <div className="playtime-profile__panel-content">
                    {freshBadges.length > 0 && (
                        <div className="playtime-profile__notice" role="status">
                            <span>
                                {freshBadges.length === 1
                                    ? t.playtime.badges.newNotice(freshBadges[0].name)(lang)
                                    : t.playtime.badges.newNoticeMany(freshBadges.length)(lang)}
                            </span>
                            <Button
                                text={t.playtime.badges.openBadge(lang)}
                                variant="primary"
                                size="small"
                                onClick={() => openBadge(freshBadges[0].id)}
                            />
                        </div>
                    )}

                    {(availableCategories.length > 0 || hasSpecial || hasAnniversary) && (
                        <div className="playtime-profile__category-filters">
                            {categoryButton(null, t.playtime.filter.all(lang))}
                            {availableCategories.map((cat) =>
                                categoryButton(cat.id, lang === 'et' ? cat.name_et : cat.name_en),
                            )}
                            {hasSpecial &&
                                categoryButton('special', t.playtime.badges.special(lang))}
                            {hasAnniversary &&
                                categoryButton('anniversary', t.playtime.anniversaryDivider(lang))}
                        </div>
                    )}

                    {mobileBar}

                    {filteredRegular.length === 0 && filteredAnniversary.length === 0 ? (
                        <div className="playtime-profile__empty">
                            <p className="playtime-profile__empty-text">
                                {t.common.noResults(lang)}
                            </p>
                        </div>
                    ) : (
                        <>
                            {filteredRegular.length > 0 && (
                                <section className="playtime-profile__section">
                                    {renderGrid(filteredRegular)}
                                </section>
                            )}
                            {filteredAnniversary.length > 0 && (
                                <>
                                    <div className="playtime-profile__divider">
                                        <div className="playtime-profile__divider-line" />
                                        <span className="playtime-profile__divider-text">
                                            {t.playtime.anniversaryDivider(lang)}
                                        </span>
                                        <div className="playtime-profile__divider-line" />
                                    </div>
                                    <section className="playtime-profile__section">
                                        {renderGrid(filteredAnniversary)}
                                    </section>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>

            {selectedBadge && (
                <BaseModal
                    isOpen
                    onClose={closeModal}
                    className="badge-modal__container"
                    showClose
                    closeAriaLabel={t.common.close(lang)}
                    outsideContent={
                        <div className="badge-nav-arrows" onClick={(e) => e.stopPropagation()}>
                            <Button
                                text=""
                                iconLeft={ArrowLeft}
                                iconOnly
                                variant="arrow"
                                size="small"
                                isDisabled={visibleIds.length < 2 || isRevealing}
                                onClick={() => step(-1)}
                                buttonProps={{ 'aria-label': t.playtime.badges.prevBadge(lang) }}
                            />
                            <Button
                                text=""
                                iconLeft={ArrowRight}
                                iconOnly
                                variant="arrow"
                                size="small"
                                isDisabled={visibleIds.length < 2 || isRevealing}
                                onClick={() => step(1)}
                                buttonProps={{ 'aria-label': t.playtime.badges.nextBadge(lang) }}
                            />
                        </div>
                    }
                >
                    <BadgeModal
                        badge={selectedBadge}
                        lang={lang}
                        isPeeking={isPeeking}
                        onTogglePeek={setPeeking}
                        reveal={
                            revealPhase
                                ? {
                                      phase: revealPhase,
                                      onRevealEnd: () => {
                                          setRevealPhase('done')
                                          markSeen(selectedBadge.id)
                                      },
                                  }
                                : null
                        }
                        claim={
                            canClaim
                                ? {
                                      lang,
                                      comment: claimComment,
                                      onCommentChange: setClaimComment,
                                      files: claimFiles,
                                      onFilesChange: setClaimFiles,
                                      isSubmitting,
                                      onSubmit: () => void submitClaim(),
                                  }
                                : null
                        }
                    />
                </BaseModal>
            )}
        </PageShell>
    )
}
